import type { Editor } from '@tiptap/react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { type KeyboardEvent, useId, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { Field, FieldDescription, FieldLabel } from '@ui/components/base/field';
import { Input } from '@ui/components/base/input';
import { CustomIconButton, type CustomIconButtonProps } from '@ui/components/buttons/customIconButton';
import { insertTemplateImage } from '@ui/components/editor/richTextEditorCommands';
import {
  applyTemplateLinkDraft,
  canApplyTemplateLink,
  isTemplateImageSource,
  readTemplateLinkDraft,
  removeTemplateLink,
  type TemplateLinkDraft,
} from '@ui/components/editor/templateLinkUtils';
import { useEditorFocusReturn } from '@ui/components/editor/useEditorFocusReturn';
import { ImageIcon, LinkIcon } from '@ui/components/icons';
import { Popover } from '@ui/components/popovers/Popover';

const ENTER_KEY = 'Enter';

const EMPTY_URL_DRAFT: TemplateLinkDraft = { url: '', text: '', originalText: '', from: 0, to: 0, editing: false };

export type RichTextUrlKind = 'link' | 'image';

interface UrlPopoverConfiguration {
  icon: CustomIconButtonProps['icon'];
  labelId: string;
  urlLabelId: string;
  urlPlaceholderId: string;
  urlHintId?: string;
  textLabelId: string;
  submitLabelId: string;
  readDraft: (editor: Editor) => TemplateLinkDraft;
  canSubmit: (editor: Editor, draft: TemplateLinkDraft) => boolean;
  submit: (editor: Editor, draft: TemplateLinkDraft) => boolean;
}

const insertImageDraft = (editor: Editor, draft: TemplateLinkDraft): boolean => {
  const alt = draft.text.trim();
  return insertTemplateImage(editor.chain(), { src: draft.url, ...(isEmpty(alt) ? {} : { alt }) });
};

const URL_POPOVER_CONFIGURATIONS: Record<RichTextUrlKind, UrlPopoverConfiguration> = {
  link: {
    icon: LinkIcon,
    labelId: 'richTextEditor.link',
    urlLabelId: 'richTextEditor.linkPrompt',
    urlPlaceholderId: 'richTextEditor.linkUrlPlaceholder',
    textLabelId: 'richTextEditor.linkText',
    submitLabelId: 'richTextEditor.applyLink',
    readDraft: readTemplateLinkDraft,
    canSubmit: canApplyTemplateLink,
    submit: applyTemplateLinkDraft,
  },
  image: {
    icon: ImageIcon,
    labelId: 'richTextEditor.image',
    urlLabelId: 'richTextEditor.imagePrompt',
    urlPlaceholderId: 'richTextEditor.imageUrlPlaceholder',
    urlHintId: 'richTextEditor.imageUrlHint',
    textLabelId: 'richTextEditor.imageAlt',
    submitLabelId: 'richTextEditor.insertImage',
    readDraft: () => EMPTY_URL_DRAFT,
    canSubmit: (_editor, draft) => isTemplateImageSource(draft.url),
    submit: insertImageDraft,
  },
};

interface RichTextUrlPopoverProps {
  editor: Editor;
  kind: RichTextUrlKind;
  active?: boolean;
}

export const RichTextUrlPopover = ({ editor, kind, active }: RichTextUrlPopoverProps) => {
  const { formatMessage } = useIntl();
  const urlInputId = useId();
  const textInputId = useId();
  const [open, setOpen] = useState<boolean>(false);
  const [draft, setDraft] = useState<TemplateLinkDraft>(EMPTY_URL_DRAFT);
  const { returnFocusToEditorOnClose, finalFocus } = useEditorFocusReturn(editor);
  const configuration = URL_POPOVER_CONFIGURATIONS[kind];
  const label = formatMessage({ id: configuration.labelId });

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(configuration.readDraft(editor));
    }
    setOpen(nextOpen);
  };

  const closeOnEditor = () => {
    returnFocusToEditorOnClose();
    setOpen(false);
  };

  const submit = () => {
    if (configuration.submit(editor, draft)) {
      closeOnEditor();
    }
  };

  const removeLink = () => {
    removeTemplateLink(editor, draft);
    closeOnEditor();
  };

  const handleInputKeyDown = (keyboardEvent: KeyboardEvent<HTMLInputElement>) => {
    if (keyboardEvent.key !== ENTER_KEY) {
      return;
    }
    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    submit();
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      align="start"
      className="w-80 gap-3"
      finalFocus={finalFocus}
      trigger={
        <CustomIconButton
          type="button"
          icon={configuration.icon}
          variant={active ? ButtonVariant.SECONDARY : ButtonVariant.GHOST}
          aria-pressed={active}
          aria-label={label}
          tooltipText={label}
        />
      }
    >
      <Field>
        <FieldLabel htmlFor={urlInputId}>{formatMessage({ id: configuration.urlLabelId })}</FieldLabel>
        <Input
          id={urlInputId}
          value={draft.url}
          placeholder={formatMessage({ id: configuration.urlPlaceholderId })}
          onChange={(changeEvent) => setDraft({ ...draft, url: changeEvent.target.value })}
          onKeyDown={handleInputKeyDown}
        />
        {isNil(configuration.urlHintId) ? null : (
          <FieldDescription>{formatMessage({ id: configuration.urlHintId })}</FieldDescription>
        )}
      </Field>
      <Field>
        <FieldLabel htmlFor={textInputId}>{formatMessage({ id: configuration.textLabelId })}</FieldLabel>
        <Input
          id={textInputId}
          value={draft.text}
          onChange={(changeEvent) => setDraft({ ...draft, text: changeEvent.target.value })}
          onKeyDown={handleInputKeyDown}
        />
      </Field>
      <Div row className="items-center justify-end gap-2">
        {draft.editing ? (
          <Button type="button" variant={ButtonVariant.DESTRUCTIVE} size="sm" className="mr-auto" onClick={removeLink}>
            {formatMessage({ id: 'richTextEditor.removeLink' })}
          </Button>
        ) : null}
        <Button type="button" size="sm" disabled={!configuration.canSubmit(editor, draft)} onClick={submit}>
          {formatMessage({ id: configuration.submitLabelId })}
        </Button>
      </Div>
    </Popover>
  );
};
